import { getApiUrl } from '@/api/config'
import { useAuth } from '@/contexts/AuthContext'
import type { components } from '@/types/api/v1'
import { useEffect, useRef, useState } from 'react'

export type UserTask = components['schemas']['UserTask']

interface UserTaskFrame {
  user_tasks: UserTask[]
}

const RECONNECT_BACKOFF_MS = [1000, 2000, 5000, 10000]

const POLICY_VIOLATION = 1008

export function streamUrl(token: string): string {
  const url = new URL(getApiUrl('/user_task/stream'), window.location.origin)
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
  url.searchParams.set('token', token)
  return url.toString()
}

export function useUserTaskStream(): UserTask[] {
  const { isAuthenticated } = useAuth()
  const [tasks, setTasks] = useState<UserTask[]>([])
  // Survives re-renders so a reconnect loop isn't restarted by an unrelated render.
  const attemptRef = useRef(0)

  useEffect(() => {
    const token = localStorage.getItem('accessToken')
    if (!isAuthenticated || !token) {
      setTasks([])
      return
    }

    let socket: WebSocket | null = null
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined
    let closedByUs = false

    const connect = () => {
      socket = new WebSocket(streamUrl(token))

      socket.onopen = () => {
        attemptRef.current = 0
      }

      socket.onmessage = (event) => {
        try {
          const frame: UserTaskFrame = JSON.parse(event.data)
          setTasks(frame.user_tasks ?? [])
        } catch {
          // Ignore unparseable results
        }
      }

      socket.onclose = (event) => {
        if (closedByUs) return
        if (event.code === POLICY_VIOLATION) return
        const delay = RECONNECT_BACKOFF_MS[attemptRef.current]
        if (delay === undefined) return
        attemptRef.current += 1
        reconnectTimer = setTimeout(connect, delay)
      }
    }

    connect()

    return () => {
      closedByUs = true
      clearTimeout(reconnectTimer)
      socket?.close()
    }
  }, [isAuthenticated])

  return tasks
}
